import React, { useMemo } from 'react';
import { useGPS } from '../../contexts/GPSContext';
import { safeParseDate } from '../../utils/dateUtils';
import { getAdminBadge } from '../../utils/getAdminBadge';

const BUCKETS = [
  { key: '0-7', label: '0 – 7 jours', hint: 'récents', max: 7, color: '#16a866', tone: 'text-[#16a866]' },
  { key: '8-30', label: '8 – 30 jours', hint: 'en cours', max: 30, color: '#3cc27e', tone: 'text-[#2a9d63]' },
  { key: '30-90', label: '30 – 90 jours', hint: 'à surveiller', max: 90, color: '#f5a623', tone: 'text-[#d4880f]' },
  { key: '90+', label: '+ de 90 jours', hint: 'critique SLA', max: Infinity, color: '#e5484d', tone: 'text-[#e5484d]' },
];

/**
 * "Backlog par ancienneté" — distribution of the open backlog by age since
 * creation, measured against the end of the selected period (not now).
 */
const GPSAgingChart = ({ stats }) => {
  const gps = useGPS();

  const { counts, total } = useMemo(() => {
    // Reference date for ageing = end of the selected period, but never in the
    // future: a future endDate (e.g. filtering the whole current year) would make
    // every open ticket appear older than it is. Clamp to "now".
    const ref = Math.min(new Date(gps.endDate).getTime(), Date.now());
    const dayMs = 1000 * 60 * 60 * 24;
    const c = { '0-7': 0, '8-30': 0, '30-90': 0, '90+': 0 };
    (stats.ongoingTickets || []).forEach((issue) => {
      const created = safeParseDate(issue.created);
      if (!created) return;
      const ageDays = (ref - created.getTime()) / dayMs;
      if (ageDays <= 7) c['0-7'] += 1;
      else if (ageDays <= 30) c['8-30'] += 1;
      else if (ageDays <= 90) c['30-90'] += 1;
      else c['90+'] += 1;
    });
    return { counts: c, total: (stats.ongoingTickets || []).length };
  }, [stats.ongoingTickets, gps.endDate]);

  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

  return (
    <section className="bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-gray-900 rounded-xl p-4 sm:p-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="inline-flex items-center justify-center rounded-lg w-9 h-9 bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5]">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 1.5" /><path d="M5 3 2 6" /><path d="m22 6-3-3" />
          </svg>
        </span>
        <span className="inline-flex items-center text-base font-bold text-gray-900 dark:text-white">{getAdminBadge()} <span className="ml-1">Backlog par ancienneté</span></span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Répartition des {total.toLocaleString('fr-FR')} tickets ouverts par âge depuis leur création
        </span>
      </div>

      {/* stacked proportion bar */}
      <div className="flex h-4 rounded-lg overflow-hidden my-3 gap-0.5">
        {BUCKETS.map((b) => (
          <div key={b.key} style={{ width: `${pct(counts[b.key])}%`, background: b.color }} title={`${b.label} — ${pct(counts[b.key])}%`} />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5">
        {BUCKETS.map((b) => (
          <div
            key={b.key}
            className="border border-gray-200 dark:border-gray-800 rounded-xl p-4"
            style={{ borderLeft: `4px solid ${b.color}` }}
          >
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">{b.label}</div>
            <div className="text-3xl font-extrabold text-gray-900 dark:text-white my-1.5">{counts[b.key].toLocaleString('fr-FR')}</div>
            <div className={`text-xs font-semibold ${b.tone}`}>{pct(counts[b.key])} % · {b.hint}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default GPSAgingChart;
